import { getStoredToken, refreshAccessToken } from './authApi';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

async function request<T>(path: string, options: RequestInit = {}, _isRetry = false): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  // Access token expired — try a silent refresh once, then retry the
  // original call. _isRetry guards against looping if the retry also 401s.
  if (res.status === 401 && !_isRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return request<T>(path, options, true);
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return null as T;
  return res.json();
}

// ── Clients ──────────────────────────────────────────────────────────────────
export const clientsApi = {
  list: (outletId?: number) => request<any[]>(outletId ? `/clients/?outlet_id=${outletId}` : '/clients/'),
  
  create: (data: {
    name: string; email?: string; phone?: string;
    company?: string; address?: string; city?: string;
    country?: string; tax_number?: string; notes?: string;
    outlet_id?: number | null;
  }) => request<any>('/clients/', { method: 'POST', body: JSON.stringify(data) }),
  
  update: (id: number, data: any) =>
    request<any>(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  
  delete: (id: number) =>
    request<null>(`/clients/${id}`, { method: 'DELETE' }),
};

// ── Invoices ─────────────────────────────────────────────────────────────────
export const invoicesApi = {
  list: (outletId?: number) => request<any[]>(outletId ? `/invoices/?outlet_id=${outletId}` : '/invoices/'),
  
  create: (data: {
    invoice_number?: string; client_id?: number;
    currency?: string; vat_rate?: number; discount?: number;
    line_items?: any[]; notes?: string; terms?: string;
    due_date?: string; elements?: any; page_size?: string;
    outlet_id?: number | null;
  }) => request<any>('/invoices/', { method: 'POST', body: JSON.stringify(data) }),
  
  update: (id: number, data: any) =>
    request<any>(`/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  
  updateStatus: (id: number, newStatus: string) =>
    request<any>(`/invoices/${id}/status?new_status=${newStatus}`, { method: 'PATCH' }),
  
  delete: (id: number) =>
    request<null>(`/invoices/${id}`, { method: 'DELETE' }),
};


async function requestBlob(path: string, options: RequestInit = {}, _isRetry = false): Promise<Blob> {
  const token = getStoredToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401 && !_isRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) return requestBlob(path, options, true);
  }

  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.blob();
}

export const pdfApi = {
  generate: (html: string, pageWidth = 794, pageHeight = 1123, contentHeight?: number) =>
    requestBlob('/generate-pdf', {
      method: 'POST',
      body: JSON.stringify({ html, pageWidth, pageHeight, contentHeight: contentHeight ?? pageHeight }),
    }),
};




// ── Discount Rules ───────────────────────────────────────────────────────────
export const discountRulesApi = {
  list: (outletId?: number) => request<any[]>(outletId ? `/discount-rules/?outlet_id=${outletId}` : '/discount-rules/'),
  create: (data: {
    label?: string;
    metric: 'visits' | 'spend';
    threshold: number;
    type: 'percent' | 'fixed';
    value: number;
    visible_on_bill: boolean;
    outlet_id?: number | null;
  }) => request<any>('/discount-rules/', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request<any>(`/discount-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request<null>(`/discount-rules/${id}`, { method: 'DELETE' }),
};

export const marketingApi = {
  sendPromo: (data: {
    recipients: { id: string; name: string; email: string }[];
    subject: string;
    message: string;
    image_url?: string;
    image_file?: { name: string; dataUrl: string; sizeMB: number };
  }) => request<{ sent: number; failed: number; errors?: string[] }>(
    '/email/send-promo',
    { method: 'POST', body: JSON.stringify(data) }
  ),
};

export const posCounterApi = {
  next: async (prefix: string, digits: number = 5): Promise<string> => {
    const data = await request<{ number: string }>('/invoices/pos-counter/next', {
      method: 'POST',
      body: JSON.stringify({ prefix, digits }),
    });
    return data.number; // e.g. "FM-00003"
  },
};